import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Database,
  Search,
  Dna,
  FlaskConical,
  ArrowRight,
  Microscope,
  Atom,
  Activity,
  Beaker,
  Command,
  Loader2,
} from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useStatistics, useGlobalSearch } from '../api/hooks'
import { useTranslation } from '../hooks/useTranslation'
import { useCommandPaletteStore } from '../stores/commandPaletteStore'
import { useDebounce } from '../hooks/useDebounce'

/**
 * 機能カード定義
 * 翻訳キーを使用してローカライズ
 */
const getFeatures = (t: (key: string) => string) => [
  {
    icon: Database,
    title: t('features.clusterBrowser.title'),
    description: t('features.clusterBrowser.description'),
    link: '/clusters',
    color: 'cyan',
  },
  {
    icon: Search,
    title: t('features.blastSearch.title'),
    description: t('features.blastSearch.description'),
    link: '/blast',
    color: 'purple',
  },
  {
    icon: FlaskConical,
    title: t('features.reactionSearch.title'),
    description: t('features.reactionSearch.description'),
    link: '/reactions',
    color: 'green',
  },
  {
    icon: Atom,
    title: t('features.substructureSearch.title'),
    description: t('features.substructureSearch.description'),
    link: '/substructure',
    color: 'amber',
  },
]

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number | string
  icon: React.ElementType
  color: string
}) {
  const colorClasses = {
    cyan: 'from-accent-cyan/20 to-accent-cyan/5 border-accent-cyan/20',
    purple: 'from-accent-purple/20 to-accent-purple/5 border-accent-purple/20',
    green: 'from-accent-green/20 to-accent-green/5 border-accent-green/20',
    amber: 'from-accent-amber/20 to-accent-amber/5 border-accent-amber/20',
  }

  const iconColors = {
    cyan: 'text-accent-cyan',
    purple: 'text-accent-purple',
    green: 'text-accent-green',
    amber: 'text-accent-amber',
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`bg-gradient-to-br ${colorClasses[color as keyof typeof colorClasses]} border rounded-xl p-6`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-white mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <Icon className={`w-10 h-10 ${iconColors[color as keyof typeof iconColors]} opacity-80`} />
      </div>
    </motion.div>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  description,
  link,
  color,
  index,
}: {
  icon: React.ElementType
  title: string
  description: string
  link: string
  color: string
  index: number
}) {
  const borderColors = {
    cyan: 'hover:border-accent-cyan/50',
    purple: 'hover:border-accent-purple/50',
    green: 'hover:border-accent-green/50',
    amber: 'hover:border-accent-amber/50',
  }

  const iconColors = {
    cyan: 'text-accent-cyan',
    purple: 'text-accent-purple',
    green: 'text-accent-green',
    amber: 'text-accent-amber',
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Link to={link}>
        <Card
          hover
          className={`h-full transition-all duration-300 ${borderColors[color as keyof typeof borderColors]}`}
        >
          <CardContent>
            <Icon
              className={`w-12 h-12 ${iconColors[color as keyof typeof iconColors]} mb-4`}
            />
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-400 mb-4">{description}</p>
            <div className="flex items-center text-accent-cyan font-medium">
              {/* 翻訳: 探索 */}
              <ArrowRight className="w-4 h-4 ml-2" />
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  )
}

/**
 * Home Search Component with Smart Search
 */
function HomeSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounce(query, 300)
  const { data: searchResults, isLoading } = useGlobalSearch(debouncedQuery)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [searchResults])

  // Build results list
  const results = [
    ...(searchResults?.clusters || []).slice(0, 4).map((c) => ({
      type: 'cluster' as const,
      id: c.mibig_accession,
      label: c.mibig_accession,
      sublabel: `${c.organism_name} • ${c.biosyn_class?.join(', ') || ''}`,
      icon: <Database className="w-4 h-4 text-accent-cyan" />,
      path: `/clusters/${c.mibig_accession}`,
    })),
    ...(searchResults?.proteins || []).slice(0, 3).map((p) => ({
      type: 'protein' as const,
      id: `${p.mibig_accession}-${p.protein_id}`,
      label: p.protein_id,
      sublabel: `${p.mibig_accession} • ${p.product || ''}`,
      icon: <Dna className="w-4 h-4 text-accent-purple" />,
      path: `/proteins/${p.mibig_accession}/${p.protein_id}`,
    })),
    ...(searchResults?.compounds || []).slice(0, 3).map((c) => ({
      type: 'compound' as const,
      id: `${c.mibig_accession}-${c.compound}`,
      label: c.compound,
      sublabel: `${c.mibig_accession}${c.database_id ? ` • ${c.database_id}` : ''}`,
      icon: <Beaker className="w-4 h-4 text-accent-amber" />,
      path: `/clusters/${c.mibig_accession}`,
    })),
  ]

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (results[selectedIndex]) {
          navigate(results[selectedIndex].path)
          setIsOpen(false)
          setQuery('')
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  return (
    <div className="relative w-full max-w-2xl mx-auto" ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search clusters, proteins, compounds... (or press Ctrl+K)"
          className="w-full pl-12 pr-20 py-4 bg-dark-bg-secondary border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent-cyan/50 focus:ring-2 focus:ring-accent-cyan/20 transition-all text-lg"
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isLoading && query.length >= 2 && (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 bg-white/5 rounded text-xs text-gray-400">
            <Command className="w-3 h-3" />K
          </kbd>
        </div>
      </div>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {isOpen && query.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-dark-bg-secondary border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {results.length > 0 ? (
              <div className="max-h-80 overflow-y-auto py-2">
                {results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => {
                      navigate(result.path)
                      setIsOpen(false)
                      setQuery('')
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      selectedIndex === index
                        ? 'bg-accent-cyan/10 text-white'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <span className={selectedIndex === index ? 'text-accent-cyan' : 'text-gray-400'}>
                      {result.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.label}</div>
                      <div className="text-xs text-gray-500 truncate">{result.sublabel}</div>
                    </div>
                    {selectedIndex === index && (
                      <ArrowRight className="w-4 h-4 text-accent-cyan flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ) : !isLoading ? (
              <div className="px-4 py-6 text-center text-gray-400">
                No results found for "{query}"
              </div>
            ) : null}

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-white/10 text-xs text-gray-500 flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↵</kbd>
                Select
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * ホームページコンポーネント
 * 日本語対応済み
 */
export function HomePage() {
  const { data: stats, isLoading } = useStatistics()
  const { t } = useTranslation()
  const features = getFeatures(t)
  const openCommandPalette = useCommandPaletteStore((state) => state.open)

  return (
    <div className="space-y-12">
      {/* ヒーローセクション */}
      <section className="text-center py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-cyan/10 border border-accent-cyan/20 rounded-full text-accent-cyan text-sm font-medium mb-6"
        >
          <Activity className="w-4 h-4" />
          {t('home.version')}
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl md:text-6xl font-bold text-white mb-6"
        >
          {t('home.heroTitle')}
          <br />
          <span className="bg-gradient-to-r from-accent-cyan via-accent-purple to-accent-cyan bg-clip-text text-transparent">
            {t('home.heroSubtitle')}
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-gray-400 max-w-3xl mx-auto mb-8"
        >
          {t('home.heroDescription')}
        </motion.p>
        {/* Smart Search Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <HomeSearch />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap gap-4 justify-center"
        >
          <Link to="/clusters">
            <Button size="lg" leftIcon={<Database className="w-5 h-5" />}>
              {t('home.browseBtn')}
            </Button>
          </Link>
          <Button size="lg" variant="secondary" leftIcon={<Search className="w-5 h-5" />} onClick={openCommandPalette}>
            {t('home.openPalette')}
          </Button>
        </motion.div>
      </section>

      {/* 統計セクション */}
      <section>
        <h2 className="text-2xl font-bold text-white mb-6">{t('home.statsTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            label={t('stats.clusters')}
            value={isLoading ? '...' : stats?.total_clusters || 0}
            icon={Database}
            color="cyan"
          />
          <StatCard
            label={t('stats.genes')}
            value={isLoading ? '...' : stats?.total_genes || 0}
            icon={Dna}
            color="purple"
          />
          <StatCard
            label={t('stats.structures')}
            value={isLoading ? '...' : stats?.total_structures || 0}
            icon={Microscope}
            color="green"
          />
          <StatCard
            label={t('stats.compounds')}
            value={isLoading ? '...' : stats?.total_compounds || 0}
            icon={FlaskConical}
            color="amber"
          />
        </div>
      </section>

      {/* 機能セクション */}
      <section>
        <h2 className="text-2xl font-bold text-white mb-6">{t('home.featuresTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={feature.link} {...feature} index={index} />
          ))}
        </div>
      </section>

      {/* クイック検索セクション */}
      <section className="glass-card p-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {t('home.quickSearch')}
            </h3>
            <p className="text-gray-400">
              {t('home.quickSearchDesc')} <kbd className="px-2 py-1 bg-white/10 rounded text-sm mx-1">{t('home.quickSearchKey')}</kbd>
              {t('home.quickSearchHelp')}
            </p>
          </div>
          <Button
            variant="secondary"
            leftIcon={<Search className="w-4 h-4" />}
            onClick={openCommandPalette}
          >
            {t('home.openPalette')}
          </Button>
        </div>
      </section>
    </div>
  )
}
